var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable no-await-in-loop */
import { PollUntil } from 'poll-until-promise';
import { generateSwirrlJson } from './createSwirrlJson';
import { generateSubsettingJson } from './createOpendapJson';
import { Status, OPERATION, SUBSETTING, } from '../../types/searchTypes';
import { BASEURL } from '../../components/App/ApplicationConfig';
import { createNotebook, deleteStaleNotebook, checkSnapshotExists, deleteStaleSession, runWorkflow, } from './swirrlApiMethods';
const throttleRequests = (ms) => new Promise((resolve) => {
    setTimeout(resolve, ms);
});
const generateMessage = (hasSnapshot, oldSessionId) => {
    if (oldSessionId) {
        if (hasSnapshot) {
            return 'SWIRRL has deleted your old notebook. All associated data will be kept for use in created snapshot.';
        }
        return 'SWIRRL has deleted your old notebook and all associated data.';
    }
    return 'No sessionId provided. SWIRRL could not delete your old notebook.';
};
export const handleCreateNotebook = (setNotebookProperties, notebookProperties, operationInUse, fileListObject, user, setUser, datasetCollection, subsettingParameters, subsettingMode = SUBSETTING.NONE) => {
    let notebookURL = '';
    const statusCodes = [];
    setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.GENERATING, redirectUrl: '' }));
    if (user && user.notebookId && user.sessionId) {
        deleteStaleNotebook(user.notebookId)
            .then(() => __awaiter(void 0, void 0, void 0, function* () {
            const hasSnapshot = yield checkSnapshotExists(user.sessionId);
            yield deleteStaleSession(user.sessionId, hasSnapshot);
        }))
            .catch((e) => console.log(e));
    }
    createNotebook()
        .then((createNotebookResponse) => {
        const notebook = JSON.parse(createNotebookResponse);
        if (notebook) {
            notebookURL = notebook.serviceURL;
            const oldSessionId = notebook.sessionId;
            const payloads = operationInUse === OPERATION.SUBSETTING &&
                datasetCollection &&
                subsettingParameters
                ? generateSubsettingJson(fileListObject, datasetCollection, subsettingParameters, notebook.sessionId, subsettingMode)
                : generateSwirrlJson(fileListObject, notebook.sessionId);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const pollUntilPromise = new PollUntil();
            pollUntilPromise
                .stopAfter(10 * 60 * 1000)
                .tryEvery(3000)
                .execute(() => __awaiter(void 0, void 0, void 0, function* () {
                if ((yield fetch(`${BASEURL}/notebook/${notebook.id}`, {
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'include',
                    referrerPolicy: 'no-referrer-when-downgrade',
                }).then((response) => response.status)) === 200) {
                    // Update React State to have the new notebook:
                    setUser(Object.assign(Object.assign({}, user), { notebookId: notebook.id, notebookURL: notebook.serviceURL, sessionId: notebook.sessionId }));
                    return true;
                }
                return false;
            }))
                .then(() => __awaiter(void 0, void 0, void 0, function* () {
                // eslint-disable-next-line no-restricted-syntax
                for (const payload of payloads) {
                    yield throttleRequests(15000);
                    const response = yield runWorkflow(payload, operationInUse, subsettingMode);
                    statusCodes.push(response);
                    if (response !== 200) {
                        console.error(`Request failed with ${response} on: ${JSON.stringify(payload)}`);
                    }
                }
            }))
                .then(() => __awaiter(void 0, void 0, void 0, function* () {
                const hasSnapshot = user && user.sessionId
                    ? yield checkSnapshotExists(user.sessionId)
                    : false;
                if (statusCodes.every((statusCode) => statusCode === 200)) {
                    setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.READY, redirectUrl: notebookURL, message: generateMessage(hasSnapshot, oldSessionId), workerNumber: payloads.length || 0 }));
                }
                else {
                    setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.ERROR, redirectUrl: '', error: ' (One or more workflows could not be started)' }));
                }
            }))
                .catch((err) => console.error(err));
        }
    })
        .catch((err) => {
        setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.ERROR, redirectUrl: '', error: '(SWIRRL url not reachable)' }));
        console.error(err);
    });
};
export const handleResumeNotebook = (setNotebookProperties, notebookProperties, operationInUse, fileListObject, user, datasetCollection, subsettingParameters, subsettingMode = SUBSETTING.NONE) => {
    const { sessionId } = user;
    const serviceURL = user.notebookURL;
    if (!sessionId || !serviceURL) {
        setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.ERROR, redirectUrl: '', error: '(sessionId or serviceURL missing)' }));
        console.error('handleResumeNotebook failed: sessionId or serviceURL missing');
        return;
    }
    const payloads = operationInUse === OPERATION.SUBSETTING &&
        datasetCollection &&
        subsettingParameters &&
        subsettingMode
        ? generateSubsettingJson(fileListObject, datasetCollection, subsettingParameters, sessionId, subsettingMode)
        : generateSwirrlJson(fileListObject, sessionId);
    const statusCodes = [];
    setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.GENERATING, redirectUrl: serviceURL }));
    Promise.resolve(JSON.stringify({
        sessionId,
        serviceURL,
    }))
        .then(() => __awaiter(void 0, void 0, void 0, function* () {
        // eslint-disable-next-line no-restricted-syntax
        for (const payload of payloads) {
            yield throttleRequests(5000);
            const response = yield runWorkflow(payload, operationInUse, subsettingMode);
            statusCodes.push(response);
            if (response !== 200) {
                console.error(`Request failed with ${response} on: ${JSON.stringify(payload)}`);
            }
        }
    }))
        .then(() => statusCodes.some((statusCode) => statusCode === 200)
        ? setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.READY, redirectUrl: serviceURL, workerNumber: payloads.length || 0 }))
        : setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.ERROR, redirectUrl: '', error: ' (One or more workflows could not be started)' })))
        .catch((err) => {
        setNotebookProperties(Object.assign(Object.assign({}, notebookProperties), { processingStatus: Status.ERROR, redirectUrl: '', error: '(SWIRRL url not reachable)' }));
        console.error(err);
    });
};
